import React, { useState, useEffect } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryLabels = {
  government_backed: '🏦 Government-Backed',
  conventional: '🤝 Conventional',
  alternative: '🚀 Alternative'
};

export default function FinancingSelector({ countryCode, value, onChange }) {
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    if (countryCode) {
      fetchProducts();
    }
  }, [countryCode]);

  useEffect(() => {
    // Find the selected product when value or products change
    if (value && Object.keys(products).length > 0) {
      for (const category of Object.keys(products)) {
        const product = products[category].find(p => p.id === value);
        if (product) {
          setSelectedProduct(product);
          break;
        }
      }
    }
  }, [value, products]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/frameworks/${countryCode}/products`);
      const data = await res.json();
      setProducts(data.products_by_category || {});
      
      // Auto-select first conventional product if no value
      if (!value && data.products_by_category?.conventional?.length > 0) {
        const defaultProduct = data.products_by_category.conventional[0];
        onChange(defaultProduct.id);
        setSelectedProduct(defaultProduct);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Button variant="outline" disabled className="w-full justify-between bg-slate-900 border-slate-700">
        <span className="text-slate-400">Loading financing options...</span>
      </Button>
    );
  }

  return (
    <div className="space-y-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full justify-between bg-slate-900 border-slate-700 hover:bg-slate-800 hover:border-slate-600 h-auto py-3"
          >
            <div className="flex items-center gap-2 text-left">
              {selectedProduct ? (
                <div>
                  <div className="text-white font-medium">{selectedProduct.name}</div>
                  <div className="text-xs text-slate-400">
                    Max LTV: {selectedProduct.rules.max_ltv}% • Min DSCR: {selectedProduct.rules.min_dscr}x
                  </div>
                </div>
              ) : (
                <span className="text-slate-400">Select financing product</span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[350px] max-h-[400px] overflow-y-auto bg-slate-900 border-slate-800">
          {Object.entries(products).map(([category, items], idx) => (
            <React.Fragment key={category}>
              {idx > 0 && <DropdownMenuSeparator className="bg-slate-800" />}
              <DropdownMenuLabel className="text-slate-400 text-xs font-normal">
                {categoryLabels[category] || category}
              </DropdownMenuLabel>
              {items.map((product) => (
                <DropdownMenuItem
                  key={product.id}
                  onClick={() => {
                    onChange(product.id);
                    setSelectedProduct(product);
                  }}
                  className={`cursor-pointer flex-col items-start ${
                    value === product.id
                      ? 'text-amber-500 bg-amber-500/10'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <div className="font-medium">{product.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    LTV: {product.rules.max_ltv}% • DSCR: {product.rules.min_dscr}x • {product.rules.max_amortization_years}yr max
                  </div>
                </DropdownMenuItem>
              ))}
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Product Info Card */}
      {selectedProduct && (
        <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-slate-300">{selectedProduct.description}</p>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
                <span>Max LTV: <span className="text-white">{selectedProduct.rules.max_ltv}%</span></span>
                <span>Min DSCR: <span className="text-white">{selectedProduct.rules.min_dscr}x</span></span>
                <span>Max Amort: <span className="text-white">{selectedProduct.rules.max_amortization_years} years</span></span>
              </div>
              {Object.keys(selectedProduct.typical_rates || {}).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.entries(selectedProduct.typical_rates).map(([term, rate]) => (
                    <span key={term} className="px-2 py-0.5 rounded bg-slate-700 text-xs text-slate-300">
                      {term.replace('_', ' ')}: {rate}%
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
